import { DateTime } from "luxon";

import { Client } from "@/Common/Api/Client";
import { PopupModel } from "@/Common/Models/UI/PopupModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { AuthDetails } from "./ResponseModels/AuthDetails";
import { GetAuthenticationDetailsResponse } from "./ResponseModels/GetAuthenticationDetailsResponse";

/**
 * Manages authentication with the Quebec Vaccination Registry (RVQ).
 */
class RvqAuthenticationManager {
    /**
     * The ID for the timeout to auto-renew the current RVQ token, if applicable.
     */
    private renewTokenTimeoutId: number = -1;

    /**
     * The current authentication info, if applicable.
     */
    private auth: AuthDetails | null;

    /**
     * Whether the current browser session is authenticated with the RVQ.
     */
    public get isAuthenticated(): boolean {
        return !!this.auth?.IsAuthenticated;
    }

    /**
     * The RVQ authentication details, if the current browser session is authenticated with the RVQ.
     */
    public get authDetails(): Readonly<AuthDetails> | null {
        return this.auth;
    }

    /**
     * Get whether the user is currently authenticated with the RVQ, fetching it from the backend if needed.
     *
     * If `showLoginPopup` is true and the user isn't authenticated, this starts the authentication flow. The user will need to re-trigger the operation after they go through the auth flow.
     *
     * @param showLoginPopup Whether to start the user auth flow if the session isn't authenticated.
     * @param bypassCache Whether to fetch the latest auth info from the backend, even if we already have it cached. This should almost always be false.
     * @returns Whether the user is currently authenticated. If the auth flow is started for `showLoginPopup`, this returns false once the popup is closed.
     */
    public async tryRequireAuthentication(showLoginPopup: boolean = true, bypassCache: boolean = false): Promise<boolean> {
        // skip if already authenticated
        // (We deliberately check `isAuthenticated` instead of `this.auth != null` here, since they may have logged in
        // since the last check. Once they authenticate, they'll stay authenticated as long as this session is open.)
        if (!bypassCache && this.isAuthenticated)
            return true;

        // fetch auth status from backend
        const response = await Client.postAsync<GetAuthenticationDetailsResponse>(ServerOperations.Server.QuebecVaccinationRegistry_GetAuthDetails, {});
        this.auth = null;

        // on error
        if (response.errors.any()) {
            sessionStore.showPopup(response.errors);

            this.autoRenewToken(null);

            return false;
        }

        // on authenticated
        if (response.data.Auth.IsAuthenticated) {
            this.auth = response.data.Auth;
            this.autoRenewToken(DateTime.local().plus({ seconds: response.data.Auth.RefreshExpiresInSeconds }));

            return true;
        }

        // else not authenticated
        this.autoRenewToken(null);
        if (showLoginPopup) {
            return new Promise<boolean>(resolve =>
                sessionStore.showPopup(this.getWarningPopupModel(response.data.LoginURL, () => resolve(false)))
            );
        }
        return false;
    }

    /**
     * Get whether the current user can edit an immunization.
     * @param immunization The immunization to check, or the RVQ username which previously submitted the immunization to the RVQ (if any).
     * @param showLoginPopup Whether to start the user auth flow if the session isn't authenticated, and authentication is required to edit the immunization.
     */
    public canEdit(immunization: MedicalItemImmunization | string | null, showLoginPopup: boolean = true): boolean {
        const submittedToRvqBy = typeof immunization === "string"
            ? immunization
            : immunization?.SubmittedToQuebecRegistryBy;

        // non-synced immunization, no permissions needed
        if (!submittedToRvqBy)
            return true;

        // can edit any immunization
        if (this.auth?.IsCorrector)
            return true;

        // else must be able to submit to RVQ
        return this.canSubmitToRvq(submittedToRvqBy, showLoginPopup);
    }

    /**
     * Get whether the current user can submit or resubmit an immunization to the RVQ.
     * @param immunization The immunization to check, or the RVQ username which previously submitted the immunization to the RVQ (if any).
     * @param showLoginPopup Whether to start the user auth flow if the session isn't authenticated.
     */
    public canSubmitToRvq(immunization: MedicalItemImmunization | string | null, showLoginPopup: boolean = true): boolean {
        const submittedToRvqBy = typeof immunization === "string"
            ? immunization
            : immunization?.SubmittedToQuebecRegistryBy;

        if (!this.isAuthenticated) {
            if (showLoginPopup)
                this.tryRequireAuthentication();
            return false;
        }

        return (
            !submittedToRvqBy
            || submittedToRvqBy === this.auth.Username
            || this.auth.IsCorrector
        );
    }

    /**
     * Handle auto-renewal when the RVQ session is active. This starts a recurring timer if the provided expiry date is non-null.
     * @param rvqTokenExpiration The UTC date when the current token expires.
     */
    private async autoRenewToken(rvqTokenExpiration: DateTime): Promise<void> {
        // reset
        clearTimeout(this.renewTokenTimeoutId);
        if (!rvqTokenExpiration)
            return;

        // renew now if needed
        if (rvqTokenExpiration.minus({ seconds: 150 }) < DateTime.local()) {
            await this.tryRequireAuthentication(false, true);
            return; // tryRequireAuthentication will call autoRenewToken with the new token
        }

        // set timer for next check
        this.renewTokenTimeoutId = window.setTimeout(() => this.autoRenewToken(rvqTokenExpiration), 60000);
    }

    /**
     * Get the model for a popup indicating that the user must login to the RVQ.
     * @param url The login URL to open if the user accepts login.
     * @param onClose The callback to invoke when the user closes the popup.
     */
    private getWarningPopupModel(url: string, onClose: () => void): PopupModel {
        return new PopupModel({
            title: cacheStore.GetString("QuebecVaccinationRegistry_AuthenticationRequiredLabel"),
            content: cacheStore.GetString("QuebecVaccinationRegistry_LoginINSPQWarning"),
            cancelAction: () => onClose(),
            showCancel: true,
            okLabel: cacheStore.GetString("ContinueLabel"),
            okAction: () => {
                onClose();
                window.open(url, "_blank");
            }
        });
    }
}

export { RvqAuthenticationManager };
